/** SynthController — manages voice allocation and parameter routing.
 *
 * Receives an already-initialized AudioContext and WASM node factory,
 * then creates/destroys voice chains (oscillator → filter → envelope)
 * in response to noteOn/noteOff events. Each oscillator layer has its
 * own ADSR envelope and filter settings.
 */

import type { AudioEnv, WasmNodeFactory } from "../initAudio.ts";
import type {
	ADSRParams,
	DelayParams,
	FilterParams,
	LimiterParams,
	OscillatorLayer,
	SynthParams,
	Voice,
	VoiceLayer,
} from "../types/synth.ts";
import {
	DEFAULT_PARAMS,
	MAX_POLYPHONY,
	WAVEFORM_VALUES,
} from "../types/synth.ts";
import type { ChorusParams } from "./Chorus.ts";
import { Chorus } from "./Chorus.ts";
import { Limiter } from "./Limiter.ts";
import { noteToFreq } from "./noteUtils.ts";
import { StereoDelay } from "./StereoDelay.ts";
import { trace } from "./synthTracer.ts";
import type { WavetableId } from "./wavetables.ts";
import { periodicWaveFromId, periodicWaveFromSamples } from "./wavetables.ts";

/** Fade-out duration (seconds) when force-stopping a voice. */
const VOICE_STEAL_FADE = 0.02 as const;

/** Set an AudioParam value on an AudioWorkletNode at the current time. */
function setParam(
	ctx: AudioContext,
	node: AudioWorkletNode,
	name: string,
	value: number,
): void {
	const parameters = node.parameters as Map<string, AudioParam>;
	const param = parameters.get(name);
	if (param) {
		param.cancelScheduledValues(0);
		param.setValueAtTime(value, ctx.currentTime);
	}
}

/** Calculate the frequency for a note + layer offsets + global cent offset. */
function layerFreq(
	note: number,
	layer: OscillatorLayer,
	globalCentOffset: number,
): number {
	const totalCents =
		layer.semitoneOffset * 100 + layer.centOffset + globalCentOffset;
	return noteToFreq(note) * 2 ** (totalCents / 1200);
}

/** Get the maximum release time across all oscillator layers. */
function maxReleaseTime(layers: readonly OscillatorLayer[]): number {
	let max = 0.3;
	for (const l of layers) {
		if (l.adsr.release > max) max = l.adsr.release;
	}
	return max;
}

/** The main synth controller. */
export class SynthController {
	private readonly context: AudioContext;
	private readonly factory: WasmNodeFactory;
	private readonly masterGain: GainNode;
	/** Synth-level stereo panner — pans the entire synth output. */
	private readonly masterPanner: StereoPannerNode;
	private readonly analyser: AnalyserNode;
	private readonly delay: StereoDelay;
	private readonly chorus: Chorus;
	private readonly limiter: Limiter;
	/** Mute gain node — set to 0 when muted, 1 when audible. */
	private readonly muteGain: GainNode;
	/** The node this synth's output is connected to (master bus or destination). */
	private readonly outputNode: AudioNode;
	private voices: Voice[] = [];
	/** Voices in their release phase, pending cleanup. */
	private releasingVoices: Voice[] = [];
	private params: SynthParams = { ...DEFAULT_PARAMS };
	/** Global cent detune offset (−100 to +100). */
	private centOffset = 0;

	constructor(env: AudioEnv, outputNode?: AudioNode) {
		this.context = env.context;
		this.factory = env.factory;
		this.outputNode = outputNode ?? this.context.destination;

		// Create master gain node
		this.masterGain = this.context.createGain();
		this.masterGain.gain.value = this.params.masterGain;

		// Create master panner for overall synth stereo position
		this.masterPanner = this.context.createStereoPanner();
		this.masterPanner.pan.value = 0;

		// Create stereo delay effect
		this.delay = new StereoDelay(this.context, this.params.tempo);

		// Create Juno-60 style chorus effect
		this.chorus = new Chorus(this.context);

		// Create limiter/compressor effect
		this.limiter = new Limiter(this.context);

		// Create mute gain node for solo/mute control
		this.muteGain = this.context.createGain();
		this.muteGain.gain.value = 1;

		// Create analyser for visualization
		this.analyser = this.context.createAnalyser();
		this.analyser.fftSize = 2048;

		// Route: masterGain → masterPanner → chorus → delay → limiter → muteGain → outputNode + analyser
		this.masterGain.connect(this.masterPanner);
		this.masterPanner.connect(this.chorus.input);
		this.chorus.output.connect(this.delay.input);
		this.delay.output.connect(this.limiter.input);
		this.limiter.output.connect(this.muteGain);
		this.muteGain.connect(this.outputNode);
		this.muteGain.connect(this.analyser);

		trace("controller", "init", {
			ctxState: this.context.state,
			sampleRate: this.context.sampleRate,
			masterGain: this.params.masterGain,
		});
	}

	/** The AudioContext this controller is connected to. */
	get audioContext(): AudioContext {
		return this.context;
	}

	/** The AnalyserNode for waveform visualization. */
	get analyserNode(): AnalyserNode {
		return this.analyser;
	}

	/** Whether the controller is ready (always true once constructed). */
	get isReady(): boolean {
		return true;
	}

	/** Current active voice count. */
	get activeVoices(): number {
		return this.voices.length;
	}

	/** Current synth parameters. */
	get currentParams(): Readonly<SynthParams> {
		return this.params;
	}

	/** Create a single voice layer for a given note, oscillator layer config, and velocity. */
	private createVoiceLayer(
		note: number,
		oscLayer: OscillatorLayer,
		velocity: number,
		extraCents = 0,
	): VoiceLayer {
		const freq = layerFreq(note, oscLayer, this.centOffset + extraCents);
		const ctx = this.context;

		const flt = new this.factory.FilterNode(
			ctx,
			oscLayer.filter.cutoff,
			oscLayer.filter.resonance,
		);
		const env = new this.factory.EnvelopeNode(ctx);
		const layerGain = ctx.createGain();
		layerGain.gain.value = oscLayer.gain;

		// Per-layer stereo panner
		const panner = ctx.createStereoPanner();
		panner.pan.value = oscLayer.pan ?? 0;

		setParam(ctx, flt.node, "cutoff", oscLayer.filter.cutoff);
		setParam(ctx, flt.node, "resonance", oscLayer.filter.resonance);
		setParam(ctx, env.node, "attack", oscLayer.adsr.attack);
		setParam(ctx, env.node, "decay", oscLayer.adsr.decay);
		setParam(ctx, env.node, "sustain", oscLayer.adsr.sustain);
		setParam(ctx, env.node, "release", oscLayer.adsr.release);
		setParam(
			ctx,
			env.node,
			"velocity",
			Math.min(Math.max(velocity, 1), 127) / 127,
		);

		let oscillator: AudioNode;
		let nativeOsc: OscillatorNode | null = null;

		if (oscLayer.waveform === "wavetable") {
			// Use a native Web Audio OscillatorNode with a PeriodicWave
			nativeOsc = ctx.createOscillator();
			nativeOsc.frequency.value = freq;

			const periodicWave =
				oscLayer.wavetableId === "custom" && oscLayer.customWavetable
					? periodicWaveFromSamples(ctx, oscLayer.customWavetable)
					: periodicWaveFromId(
							ctx,
							(oscLayer.wavetableId ?? "mellow-saw") as WavetableId,
						);

			if (periodicWave) {
				nativeOsc.setPeriodicWave(periodicWave);
			}
			nativeOsc.start();
			oscillator = nativeOsc;
		} else {
			// Use WASM AudioWorklet oscillator
			const osc = new this.factory.OscillatorNode(ctx, freq);
			setParam(ctx, osc.node, "frequency", freq);
			setParam(ctx, osc.node, "waveform", WAVEFORM_VALUES[oscLayer.waveform]);
			oscillator = osc.node;

			const wasmOscRef = osc;
			const result: VoiceLayer = {
				oscillator,
				filter: flt.node,
				envelope: env.node,
				gain: layerGain,
				panner,
				wasmRefs: {
					oscillatorRef: wasmOscRef,
					filterRef: flt,
					envelopeRef: env,
				},
				layerId: oscLayer.id,
				nativeOsc: null,
			};

			// Connect: osc → filter → envelope → layerGain → panner
			oscillator.connect(flt.node);
			flt.node.connect(env.node);
			env.node.connect(layerGain);
			layerGain.connect(panner);

			setParam(ctx, env.node, "gate", 1.0);
			return result;
		}

		// Connect: nativeOsc → filter → envelope → layerGain → panner
		oscillator.connect(flt.node);
		flt.node.connect(env.node);
		env.node.connect(layerGain);
		layerGain.connect(panner);

		setParam(ctx, env.node, "gate", 1.0);

		return {
			oscillator,
			filter: flt.node,
			envelope: env.node,
			gain: layerGain,
			panner,
			wasmRefs: {
				oscillatorRef: null,
				filterRef: flt,
				envelopeRef: env,
			},
			layerId: oscLayer.id,
			nativeOsc,
		};
	}

	/** Trigger a note on. Allocates a voice (with all layers) and starts envelopes.
	 * @param note MIDI note number
	 * @param velocity Optional MIDI velocity (1–127). Defaults to 127 (full volume).
	 * @param extraCents Optional per-note cent adjustment (e.g. just intonation offset).
	 */
	noteOn(note: number, velocity = 127, extraCents = 0): void {
		trace("controller", "noteOn:enter", {
			note,
			velocity,
			activeVoices: this.voices.length,
			releasingVoices: this.releasingVoices.length,
			ctxState: this.context.state,
			ctxTime: this.context.currentTime,
			masterGain: this.masterGain.gain.value,
		});

		if (this.voices.some((v) => v.note === note)) {
			trace("controller", "noteOn:duplicate", { note });
			return;
		}

		if (this.voices.length >= MAX_POLYPHONY) {
			const oldest = this.voices.shift();
			if (oldest) {
				trace("controller", "noteOn:voiceSteal", { stolenNote: oldest.note });
				this.fadeOutAndDisconnect(oldest);
			}
		}

		const ctx = this.context;
		const voiceGain = ctx.createGain();
		voiceGain.gain.value = 1.0;
		voiceGain.connect(this.masterGain);

		const layers = this.params.oscillatorLayers
			.filter((oscLayer) => oscLayer.enabled)
			.map((oscLayer) => {
				const vl = this.createVoiceLayer(note, oscLayer, velocity, extraCents);
				vl.panner.connect(voiceGain);
				return vl;
			});

		const voice: Voice = {
			layers,
			voiceGain,
			note,
			startTime: ctx.currentTime,
			extraCents,
		};

		this.voices.push(voice);

		trace("controller", "noteOn:done", {
			note,
			layerCount: layers.length,
			activeVoices: this.voices.length,
			ctxTime: ctx.currentTime,
		});
	}

	/** Trigger a note off. Starts the release phase of the matching voice. */
	noteOff(note: number): void {
		trace("controller", "noteOff:enter", {
			note,
			activeVoices: this.voices.length,
			activeNotes: this.voices.map((v) => v.note),
		});

		const voiceIndex = this.voices.findIndex((v) => v.note === note);
		if (voiceIndex === -1) {
			trace("controller", "noteOff:notFound", { note });
			return;
		}

		const voice = this.voices[voiceIndex]!;

		for (const layer of voice.layers) {
			setParam(this.context, layer.envelope, "gate", 0.0);
		}

		this.voices.splice(voiceIndex, 1);
		this.releasingVoices.push(voice);

		const releaseTime = maxReleaseTime(this.params.oscillatorLayers) + 0.15;

		trace("controller", "noteOff:release", {
			note,
			releaseTime,
			ctxTime: this.context.currentTime,
		});

		setTimeout(() => {
			this.disconnectVoice(voice);
			this.releasingVoices = this.releasingVoices.filter((v) => v !== voice);
			trace("controller", "noteOff:cleanup", { note });
		}, releaseTime * 1000);
	}

	/** Update oscillator layers configuration. */
	setOscillatorLayers(layers: readonly OscillatorLayer[]): void {
		this.params = { ...this.params, oscillatorLayers: layers };
	}

	/** Update a single oscillator layer and apply pitch/gain/waveform changes to active voices. */
	updateLayer(layerId: string, update: Partial<OscillatorLayer>): void {
		const layers = this.params.oscillatorLayers.map((l) =>
			l.id === layerId ? { ...l, ...update } : l,
		);
		this.params = { ...this.params, oscillatorLayers: layers };

		const updatedLayer = layers.find((l) => l.id === layerId);
		if (!updatedLayer) return;

		for (const voice of this.voices) {
			for (const vl of voice.layers) {
				if (vl.layerId !== layerId) continue;

				if (vl.nativeOsc) {
					if (
						update.semitoneOffset !== undefined ||
						update.centOffset !== undefined
					) {
						const freq = layerFreq(
							voice.note,
							updatedLayer,
							this.centOffset + voice.extraCents,
						);
						vl.nativeOsc.frequency.cancelScheduledValues(
							this.context.currentTime,
						);
						vl.nativeOsc.frequency.setValueAtTime(
							freq,
							this.context.currentTime,
						);
					}
				} else {
					if (update.waveform !== undefined) {
						setParam(
							this.context,
							vl.oscillator as AudioWorkletNode,
							"waveform",
							WAVEFORM_VALUES[update.waveform],
						);
					}
					if (
						update.semitoneOffset !== undefined ||
						update.centOffset !== undefined
					) {
						const freq = layerFreq(
							voice.note,
							updatedLayer,
							this.centOffset + voice.extraCents,
						);
						setParam(
							this.context,
							vl.oscillator as AudioWorkletNode,
							"frequency",
							freq,
						);
					}
				}

				if (update.gain !== undefined) {
					vl.gain.gain.cancelScheduledValues(0);
					vl.gain.gain.setValueAtTime(update.gain, this.context.currentTime);
				}
				if (update.pan !== undefined) {
					vl.panner.pan.cancelScheduledValues(0);
					vl.panner.pan.setValueAtTime(update.pan, this.context.currentTime);
				}
			}
		}
	}

	/** Update ADSR parameters for a specific layer on all active voices. */
	setLayerADSR(layerId: string, adsr: Partial<ADSRParams>): void {
		const layers = this.params.oscillatorLayers.map((l) =>
			l.id === layerId ? { ...l, adsr: { ...l.adsr, ...adsr } } : l,
		);
		this.params = { ...this.params, oscillatorLayers: layers };

		for (const voice of this.voices) {
			for (const vl of voice.layers) {
				if (vl.layerId === layerId) {
					if (adsr.attack !== undefined)
						setParam(this.context, vl.envelope, "attack", adsr.attack);
					if (adsr.decay !== undefined)
						setParam(this.context, vl.envelope, "decay", adsr.decay);
					if (adsr.sustain !== undefined)
						setParam(this.context, vl.envelope, "sustain", adsr.sustain);
					if (adsr.release !== undefined)
						setParam(this.context, vl.envelope, "release", adsr.release);
				}
			}
		}
	}

	/** Update filter parameters for a specific layer on all active voices. */
	setLayerFilter(layerId: string, filter: Partial<FilterParams>): void {
		const layers = this.params.oscillatorLayers.map((l) =>
			l.id === layerId ? { ...l, filter: { ...l.filter, ...filter } } : l,
		);
		this.params = { ...this.params, oscillatorLayers: layers };

		for (const voice of this.voices) {
			for (const vl of voice.layers) {
				if (vl.layerId === layerId) {
					if (filter.cutoff !== undefined)
						setParam(this.context, vl.filter, "cutoff", filter.cutoff);
					if (filter.resonance !== undefined)
						setParam(this.context, vl.filter, "resonance", filter.resonance);
				}
			}
		}
	}

	/** Update ADSR parameters for ALL layers on all active voices. */
	setADSR(adsr: Partial<ADSRParams>): void {
		const layers = this.params.oscillatorLayers.map((l) => ({
			...l,
			adsr: { ...l.adsr, ...adsr },
		}));
		this.params = { ...this.params, oscillatorLayers: layers };

		for (const voice of this.voices) {
			for (const layer of voice.layers) {
				if (adsr.attack !== undefined)
					setParam(this.context, layer.envelope, "attack", adsr.attack);
				if (adsr.decay !== undefined)
					setParam(this.context, layer.envelope, "decay", adsr.decay);
				if (adsr.sustain !== undefined)
					setParam(this.context, layer.envelope, "sustain", adsr.sustain);
				if (adsr.release !== undefined)
					setParam(this.context, layer.envelope, "release", adsr.release);
			}
		}
	}

	/** Update filter parameters for ALL layers on all active voices. */
	setFilter(filter: Partial<FilterParams>): void {
		const layers = this.params.oscillatorLayers.map((l) => ({
			...l,
			filter: { ...l.filter, ...filter },
		}));
		this.params = { ...this.params, oscillatorLayers: layers };

		for (const voice of this.voices) {
			for (const layer of voice.layers) {
				if (filter.cutoff !== undefined)
					setParam(this.context, layer.filter, "cutoff", filter.cutoff);
				if (filter.resonance !== undefined)
					setParam(this.context, layer.filter, "resonance", filter.resonance);
			}
		}
	}

	/** Update master gain. */
	setMasterGain(gain: number): void {
		this.params = { ...this.params, masterGain: gain };
		this.masterGain.gain.cancelScheduledValues(0);
		this.masterGain.gain.setValueAtTime(gain, this.context.currentTime);
	}

	/** Set the overall synth stereo pan position (−1 to +1). */
	setMasterPan(pan: number): void {
		this.params = { ...this.params, pan };
		this.masterPanner.pan.cancelScheduledValues(0);
		this.masterPanner.pan.setValueAtTime(pan, this.context.currentTime);
	}

	/** Mute or unmute this synth's audio output (for solo/mute). */
	setMuted(muted: boolean): void {
		this.muteGain.gain.cancelScheduledValues(0);
		this.muteGain.gain.setValueAtTime(muted ? 0 : 1, this.context.currentTime);
	}

	/** Set global cent detune offset and update all active voices. */
	setCentOffset(cents: number): void {
		this.centOffset = cents;
		for (const voice of this.voices) {
			for (const vl of voice.layers) {
				const oscLayer = this.params.oscillatorLayers.find(
					(l) => l.id === vl.layerId,
				);
				if (oscLayer) {
					const freq = layerFreq(
						voice.note,
						oscLayer,
						cents + voice.extraCents,
					);
					if (vl.nativeOsc) {
						vl.nativeOsc.frequency.cancelScheduledValues(
							this.context.currentTime,
						);
						vl.nativeOsc.frequency.setValueAtTime(
							freq,
							this.context.currentTime,
						);
					} else {
						setParam(
							this.context,
							vl.oscillator as AudioWorkletNode,
							"frequency",
							freq,
						);
					}
				}
			}
		}
	}

	/** Set global tempo (BPM) — updates tempo-synced delay. */
	setTempo(bpm: number): void {
		this.params = { ...this.params, tempo: bpm };
		this.delay.setTempo(bpm);
	}

	/** Update delay effect parameters. */
	setDelay(update: Partial<DelayParams>): void {
		this.delay.setParams(update);
	}

	/** Get the current delay effect instance. */
	get delayEffect(): StereoDelay {
		return this.delay;
	}

	/** Update chorus effect parameters. */
	setChorus(update: Partial<ChorusParams>): void {
		this.chorus.setParams(update);
	}

	/** Get the current chorus instance. */
	get chorusEffect(): Chorus {
		return this.chorus;
	}

	/** Update limiter/compressor parameters. */
	setLimiter(update: Partial<LimiterParams>): void {
		this.limiter.setParams(update);
	}

	/** Get the current limiter instance. */
	get limiterEffect(): Limiter {
		return this.limiter;
	}

	/** Fade out a voice over a short duration, then disconnect it. */
	private fadeOutAndDisconnect(voice: Voice): void {
		try {
			const now = this.context.currentTime;
			voice.voiceGain.gain.cancelScheduledValues(0);
			voice.voiceGain.gain.setValueAtTime(voice.voiceGain.gain.value, now);
			voice.voiceGain.gain.linearRampToValueAtTime(0, now + VOICE_STEAL_FADE);
			setTimeout(
				() => this.disconnectVoice(voice),
				VOICE_STEAL_FADE * 1000 + 50,
			);
		} catch {
			this.disconnectVoice(voice);
		}
	}

	/** Disconnect and clean up all layers of a voice. */
	private disconnectVoice(voice: Voice): void {
		for (const layer of voice.layers) {
			try {
				layer.oscillator.disconnect();
				layer.filter.disconnect();
				layer.envelope.disconnect();
				layer.gain.disconnect();
				layer.panner.disconnect();
			} catch {
				// Nodes may already be disconnected
			}
			try {
				// Stop native oscillator (wavetable voices)
				layer.nativeOsc?.stop();
			} catch {
				// Already stopped
			}
			try {
				layer.wasmRefs.oscillatorRef?.free();
				layer.wasmRefs.filterRef.free();
				layer.wasmRefs.envelopeRef.free();
			} catch {
				// Already freed
			}
		}
		try {
			voice.voiceGain.disconnect();
		} catch {
			// Already disconnected
		}
	}

	/** Release all currently sounding notes (sends gate=0 to every voice). */
	allNotesOff(): readonly number[] {
		const releasedNotes: number[] = [];
		const releaseTime = maxReleaseTime(this.params.oscillatorLayers) + 0.15;

		for (const voice of [...this.voices]) {
			releasedNotes.push(voice.note);
			for (const layer of voice.layers) {
				setParam(this.context, layer.envelope, "gate", 0.0);
			}
			this.releasingVoices.push(voice);
			setTimeout(() => {
				this.disconnectVoice(voice);
				this.releasingVoices = this.releasingVoices.filter((v) => v !== voice);
			}, releaseTime * 1000);
		}
		this.voices = [];
		trace("controller", "allNotesOff", { count: releasedNotes.length });
		return releasedNotes;
	}

	/** Panic — immediately silence everything and reset to a known state.
	 *  Sends gate=0 to all voices first (graceful shutdown), then disconnects.
	 *  Does NOT touch masterGain — new notes should play immediately after panic.
	 */
	panic(): readonly number[] {
		const releasedNotes: number[] = [];
		// Gate-off all active voices before disconnecting so the Rust
		// AudioWorklet processors see gate=0 and stop outputting samples.
		for (const voice of this.voices) {
			releasedNotes.push(voice.note);
			for (const layer of voice.layers) {
				try {
					setParam(this.context, layer.envelope, "gate", 0.0);
				} catch {
					// Node may already be invalid
				}
			}
			this.disconnectVoice(voice);
		}
		for (const voice of this.releasingVoices) {
			this.disconnectVoice(voice);
		}
		this.voices = [];
		this.releasingVoices = [];
		trace("controller", "panic", { count: releasedNotes.length });
		return releasedNotes;
	}

	/** Shut down the synth and clean up all resources. */
	destroy(): void {
		for (const voice of this.voices) {
			this.disconnectVoice(voice);
		}
		for (const voice of this.releasingVoices) {
			this.disconnectVoice(voice);
		}
		this.voices = [];
		this.releasingVoices = [];
		this.masterGain.disconnect();
		this.masterPanner.disconnect();
		this.analyser.disconnect();
		this.delay.destroy();
		this.limiter.destroy();
	}
}
